import React, { useContext, useEffect, useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { CommonCollections } from '../../../../App';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useAdminCollections } from '../../../../context/AdminCollections';
import { getNominationRoundYear } from '../../../../utils/getNominationRoundYear';
import { getReportRoundYear } from '../../../../utils/getReportRoundYear.js';
import {
  DECLINE_REASON,
  NOMINATION_STATUS,
  REFEREE_STATUS,
} from '../../../../utils/constants';
import {
  createCandidateIdentifier,
  isSameCandidate,
} from '../../../../utils/candidateMatching.js';

const RefereeHistory = () => {
  const { nominationId } = useContext(CommonCollections) || {};

  const [activeTab, setActiveTab] = useState('feedback');

  const { users, declineReasons, refereeStatuses, refereeReportArchivedArray } =
    useStaticCollections();

  const { nominationArray, draftNominationArray, candidateArray } =
    useAdminCollections();

  const extractNameFromUserInfo = (fullUserInfo) => {
    if (!fullUserInfo) return 'Unknown User';

    const emailParenPattern = /\s*\([^()]*@[^()]*\)\s*$/;

    if (emailParenPattern.test(fullUserInfo)) {
      return fullUserInfo.replace(emailParenPattern, '').trim();
    }

    return fullUserInfo.trim();
  };

  let searchNominationId = nominationId;

  if (
    nominationId &&
    typeof nominationId === 'string' &&
    nominationId.includes('d')
  ) {
    const draftId = Number(nominationId.replace('d', ''));

    const draftNomination = draftNominationArray?.find(
      (draft) => draft.id === draftId,
    );

    if (draftNomination) {
      let draftData = null;
      if (draftNomination.attributes?.value) {
        try {
          draftData = JSON.parse(draftNomination.attributes.value);
        } catch (error) {
          // Malformed draft JSON — skip gracefully so the rest of the history
          // still renders.
          draftData = null;
        }
      }

      if (draftData) {
        const draftCandidateInfo = createCandidateIdentifier(draftData);

        const regularNomination = nominationArray?.find((regular) => {
          const { candidateId, nominationStatusTypeId } = regular.attributes;

          if (nominationStatusTypeId === NOMINATION_STATUS.EXPIRED) {
            return false;
          }

          const candidate = candidateArray?.find((c) => c.id === candidateId);

          if (candidate) {
            const regularCandidateInfo = createCandidateIdentifier(
              candidate.attributes,
            );
            return isSameCandidate(draftCandidateInfo, regularCandidateInfo);
          }
          return false;
        });

        if (regularNomination) {
          searchNominationId = regularNomination.id;
        }
      }
    }
  }

  const filteredRefereeReport = refereeReportArchivedArray.filter(
    (refereeReport) =>
      refereeReport.attributes.nominationId === searchNominationId &&
      refereeReport.attributes.independentAssessor === false,
  );

  const latestRoundYear =
    filteredRefereeReport.length > 0
      ? Math.max(
          ...filteredRefereeReport.map((report) => report.attributes.roundYear),
        )
      : null;

  const latestRoundReports =
    latestRoundYear === null
      ? []
      : filteredRefereeReport.filter(
          (refereeReport) =>
            refereeReport.attributes.roundYear === latestRoundYear,
        );

  const historicalRefereeReports =
    latestRoundYear === null
      ? []
      : filteredRefereeReport.filter(
          (refereeReport) =>
            refereeReport.attributes.roundYear < latestRoundYear,
        );

  const refereeTable = latestRoundReports.map((refereeReport) => {
    const {
      extensionReason,
      declineReasonId,
      declineReasonOther,
      refereeStatusId,
      refereeUserId,
      extensionReasonApprovalNote,
      roundYear,
      updatedAt,
    } = refereeReport.attributes;
    refereeReport.refereeUserId = refereeUserId;

    const fullUserInfo = users[refereeUserId];
    refereeReport.refereeUser = extractNameFromUserInfo(fullUserInfo);

    let refereeStatus =
      refereeStatusId === REFEREE_STATUS.PENDING
        ? 'No Response'
        : refereeStatuses[refereeStatusId];

    if (refereeStatusId === REFEREE_STATUS.USE_EXISTING) {
      const reportUploadedYear = getReportRoundYear(refereeReport.attributes);

      if (reportUploadedYear !== 'Not previously uploaded') {
        refereeStatus = `Use Existing Report (Submitted in ${reportUploadedYear} round)`;
      } else {
        refereeStatus = 'Use Existing Report';
      }
    }

    refereeReport.refereeStatus = refereeStatus;

    if (refereeStatusId === REFEREE_STATUS.DECLINED) {
      refereeReport.reason =
        declineReasonId === DECLINE_REASON.OTHER
          ? declineReasonOther
          : declineReasons[declineReasonId];
    } else if (refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED) {
      refereeReport.reason = extensionReason;
    } else if (refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED) {
      refereeReport.reason = extensionReasonApprovalNote;
    }
    refereeReport.round = roundYear;
    refereeReport.year = getNominationRoundYear(new Date(updatedAt));
    return refereeReport;
  });

  const historicalRefereeTable = historicalRefereeReports.map(
    (refereeReport) => {
      const {
        declineReasonId,
        declineReasonOther,
        refereeStatusId,
        refereeUserId,
        roundYear,
        createdAt,
        updatedAt,
        reminderEmailDate,
      } = refereeReport.attributes;

      const fullUserInfo = users[refereeUserId];
      const refereeUser = extractNameFromUserInfo(fullUserInfo);

      let refereeStatus =
        refereeStatusId === REFEREE_STATUS.PENDING
          ? 'No Response'
          : refereeStatuses[refereeStatusId];

      let reason = '';
      if (refereeStatusId === REFEREE_STATUS.DECLINED) {
        reason =
          declineReasonId === DECLINE_REASON.OTHER
            ? declineReasonOther
            : declineReasons[declineReasonId];
      }

      const dateToUse = reminderEmailDate ? updatedAt : createdAt;
      const year = getNominationRoundYear(new Date(dateToUse));

      if (refereeStatusId === REFEREE_STATUS.USE_EXISTING) {
        const reportUploadedYear = getReportRoundYear(refereeReport.attributes);

        if (
          reportUploadedYear !== 'Not previously uploaded' &&
          reportUploadedYear < year
        ) {
          refereeStatus = `Use Existing Report (Submitted in ${reportUploadedYear} round)`;
        } else {
          refereeStatus = 'Use Existing Report';
        }
      }

      return {
        name: refereeUser,
        year,
        status: refereeStatus,
        reason,
        roundYear,
      };
    },
  );

  const sortedHistoricalTable = historicalRefereeTable.sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  useEffect(() => {
    if (sortedHistoricalTable.length === 0 && refereeTable.length > 0) {
      setActiveTab('reports');
    } else if (sortedHistoricalTable.length > 0 && refereeTable.length === 0) {
      setActiveTab('feedback');
    } else if (sortedHistoricalTable.length > 0 && refereeTable.length > 0) {
      setActiveTab('reports');
    }
  }, [sortedHistoricalTable.length, refereeTable.length]);

  const hasFeedbackHistory = sortedHistoricalTable.length > 0;
  const hasRefereeReports = refereeTable.length > 0;

  return (
    nominationId &&
    (hasFeedbackHistory || hasRefereeReports) && (
      <>
        <div className="col-xl-12 col-lg-12 mt-xl-5 card-header">
          <h4>Referee History</h4>
        </div>

        <div className="col-xl-12 col-lg-12 mt-4">
          <Tabs
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab)}
            className="mb-3 custom-tabs"
          >
            {hasRefereeReports && (
              <Tab
                eventKey="reports"
                title={`Round ${refereeTable.length > 0 ? refereeTable[0].year : ''} reports (${refereeTable.length})`}
              >
                <div className="table-responsive">
                  <table className="table table-striped table-bordered dataTable display referee-history-table">
                    <thead className="thead-dark">
                      <tr>
                        <th className="referee-history-reports-name">
                          <b>Name</b>
                        </th>
                        <th className="referee-history-reports-year">
                          <b>Year</b>
                        </th>
                        <th className="referee-history-reports-status">
                          <b>Status</b>
                        </th>
                        <th className="referee-history-reports-reason">
                          <b>Reason</b>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {refereeTable
                        .sort((a, b) =>
                          a.refereeUser.localeCompare(b.refereeUser),
                        )
                        .map((referee, index) => (
                          <tr key={index}>
                            <td className="referee-history-cell">
                              {referee.refereeUser}
                            </td>
                            <td className="referee-history-cell">
                              {referee.year}
                            </td>
                            <td className="referee-history-cell">
                              {referee.refereeStatus}
                            </td>
                            <td className="referee-history-cell">
                              {referee.refereeStatusId ===
                              REFEREE_STATUS.EXTENSION_APPROVED ? (
                                <>
                                  <span>Reason: {referee.extensionReason}</span>
                                  <br />
                                  <span>
                                    Approval Note:{' '}
                                    {referee.extensionReasonApprovalNote}
                                  </span>
                                </>
                              ) : (
                                <span>{referee.reason}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
            )}

            {hasFeedbackHistory && (
              <Tab
                eventKey="feedback"
                title={`Feedback History (${sortedHistoricalTable.length})`}
              >
                <div className="table-responsive">
                  <table className="table table-striped table-bordered dataTable display referee-history-table">
                    <thead className="thead-dark">
                      <tr>
                        <th className="referee-history-feedback-name">
                          <b>Name</b>
                        </th>
                        <th className="referee-history-feedback-year">
                          <b>Year</b>
                        </th>
                        <th className="referee-history-feedback-status">
                          <b>Status</b>
                        </th>
                        <th className="referee-history-feedback-reason">
                          <b>Reason</b>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHistoricalTable.map((entry, index) => (
                        <tr key={index}>
                          <td className="referee-history-cell">{entry.name}</td>
                          <td className="referee-history-cell">{entry.year}</td>
                          <td className="referee-history-cell">
                            {entry.status}
                          </td>
                          <td className="referee-history-cell">
                            {entry.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
            )}
          </Tabs>
        </div>
      </>
    )
  );
};

export default RefereeHistory;
